import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import {
  AppearanceService,
  AppInfoStore,
  SOFTLINE_FEATURE_APP_INFO,
} from '@softline/application';
import { DateService, getYear, Store } from '@softline/core';
import { BreakpointService, MessageBarService } from '@softline/ui-core';
import { filter } from 'rxjs';
import { ShellEnvironment } from '../../shell.constants';
import {
  SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING,
  SOFTLINE_CONFIG_PUBLIC_SHELL_LOGO,
  SOFTLINE_TOKEN_SHELL_ENVIRONMENT,
} from '../../shell.tokens';
import { watchCssUrlVariable } from '../../functions/css-url-variable';
import { PwaUpdatePanelComponent } from '../../components/pwa-update-panel/pwa-update-panel.component';
import { buildNameFn } from '@softline/application';

const SCAN_BAR_COUNT = 13;
/** Widest bar that keeps 13 bars plus their gaps inside the 5rem tile. */
const SCAN_BAR_MAX_WIDTH = 4;
const SCAN_IDLE_MIN_MS = 5_000;
const SCAN_IDLE_MAX_MS = 15_000;
const SCAN_FIRST_IDLE_MS = 1_200;

function randomScanBarWidths(): number[] {
  return Array.from(
    { length: SCAN_BAR_COUNT },
    () => 2 + Math.floor(Math.random() * (SCAN_BAR_MAX_WIDTH - 1))
  );
}

@Component({
  selector: 'soft-public-shell',
  templateUrl: './public-shell.component.html',
  styleUrls: ['./public-shell.component.css'],
  hostDirectives: [CdkScrollable],
  imports: [RouterModule, NgTemplateOutlet, PwaUpdatePanelComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: SOFTLINE_TOKEN_SHELL_ENVIRONMENT, useValue: ShellEnvironment.PUBLIC },
  ],
})
export class PublicShellComponent implements OnInit {
  year?: string;
  messageBarService = inject(MessageBarService);
  private router = inject(Router);
  protected appearanceService = inject(AppearanceService);
  private readonly breakpointService = inject(BreakpointService);

  version = this.store.signal(SOFTLINE_FEATURE_APP_INFO, AppInfoStore.getters.version, {
    initialValue: '0',
  });

  readonly buildName = buildNameFn();

  appName = this.store.signal(SOFTLINE_FEATURE_APP_INFO, AppInfoStore.getters.name, {
    initialValue: 'SoftApps',
  });

  readonly showBranding = inject(SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING);

  private readonly logoConfig = inject(SOFTLINE_CONFIG_PUBLIC_SHELL_LOGO);
  protected readonly logoBadge = this.logoConfig.badge ?? null;
  protected readonly scanLogo = this.logoConfig.scanAnimation ?? false;
  protected readonly scanBarWidths = signal(randomScanBarWidths());
  protected readonly scanning = signal(false);
  private scanTimer: ReturnType<typeof setTimeout> | null = null;

  readonly brandingSrc = signal<string | null>(null);
  private readonly destroyRef = inject(DestroyRef);
  readonly isReturningUser = localStorage.getItem('softapps-visited') === 'true';

  readonly isMobile = this.breakpointService.isMobile;

  readonly logoSpinning = signal(false);
  readonly rotationDegrees = signal(360);

  constructor(
    private store: Store,
    private dateService: DateService
  ) {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationStart))
      .subscribe(() => this.logoSpinning.set(true));

    if (this.scanLogo) {
      this.scheduleScan(SCAN_FIRST_IDLE_MS);
      this.destroyRef.onDestroy(() => {
        if (this.scanTimer) clearTimeout(this.scanTimer);
      });
    }
  }

  private scheduleScan(idle: number): void {
    this.scanTimer = setTimeout(() => {
      this.scanTimer = null;
      this.scanBarWidths.set(randomScanBarWidths());
      this.scanning.set(true);
    }, idle);
  }

  protected onScanEnd(): void {
    this.scanning.set(false);
    this.scheduleScan(
      SCAN_IDLE_MIN_MS + Math.random() * (SCAN_IDLE_MAX_MS - SCAN_IDLE_MIN_MS)
    );
  }

  ngOnInit(): void {
    this.year = '' + getYear(this.dateService.now());
    localStorage.setItem('softapps-visited', 'true');
    if (!this.showBranding) return;
    watchCssUrlVariable(
      '--publicShellBranding',
      (url) => this.brandingSrc.set(url),
      this.destroyRef
    );
  }

  protected onBrandingError(): void {
    this.brandingSrc.set(null);
  }

  spinLogo(): void {
    if (this.logoSpinning()) return;
    this.rotationDegrees.update((d) => (d === 360 ? -360 : 360));
    this.logoSpinning.set(true);
  }

  onLogoAnimationEnd(): void {
    this.logoSpinning.set(false);
  }
}
